import sortBy from 'lodash/sortBy';
import { useCallback, useMemo, useState } from 'react';

import { ComponentColumn } from 'approot/activity-builder/components/component.constants';
import { ColumnWrapper } from 'approot/activity-builder/components/columns/columns.style';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import ActivityBuilderZoomComponentModal from 'approot/activity-builder/activity-builder-zoom-component-modal';
import ComponentsViewWrapper from 'approot/activity-builder/components/components-view-wrapper';
import {
  ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
  ComponentEventData,
  ComponentExpandModeType,
  pushComponentViewEvent,
} from '../../present/activity-builder-present-data-tracking.utils';
import StudentComponents from './student-components';
import { isStudentAuthenticated } from 'approot/students/logic/is-student-authenticated';
import type { InteractiveLessonSectionComponentFragment } from '../../lesson-builder.graphql.generated';
import {
  getAppliedColour,
  isBackgroundApplied,
  isBorderApplied,
} from 'approot/activity-builder/components/shared/component-settings-borders.utils';
import { getStyleProperties } from 'approot/activity-builder/components/components.utils';

type Props = {
  column: ComponentColumn;
  contentId: number;
  eventTrackingData?: ComponentEventData;
};

const StudentComponentsViewColumns = ({
  column,
  contentId,
  eventTrackingData,
}: Props) => {
  const sortedComponents = useMemo(
    () => sortBy(column.components, 'sort'),
    [column.components]
  );

  const [zoomedComponent, setZoomedComponent] = useState<
    InteractiveLessonSectionComponentFragment | undefined
  >();

  const { isVisible, openModal, closeModal } = useSimpleModalState();

  const handleOpenModal = useCallback(
    (
      component: InteractiveLessonSectionComponentFragment,
      expandMode?: ComponentExpandModeType
    ) => {
      setZoomedComponent(component);
      pushComponentViewEvent({
        component,
        eventType: ACTIVITY_BUILDER_EVENT_COMPONENT_EXPAND,
        expandMode,
        eventTrackingData,
        contentId,
      });
      openModal();
    },
    [contentId, eventTrackingData, openModal]
  );

  return (
    <ColumnWrapper>
      {sortedComponents.map(component => {
        return (
          <ComponentsViewWrapper
            key={component.id}
            id={component.id}
            type={component.type}
            colours={getAppliedColour(getStyleProperties(component))}
            showBorder={isBorderApplied(getStyleProperties(component))}
            showBackground={isBackgroundApplied(getStyleProperties(component))}
          >
            <StudentComponents
              component={component}
              contentId={contentId}
              eventTrackingData={eventTrackingData}
              isLoggedIn={isStudentAuthenticated()}
              handleOpenModal={handleOpenModal}
            />
          </ComponentsViewWrapper>
        );
      })}

      {isVisible && zoomedComponent && (
        <ActivityBuilderZoomComponentModal
          isStudent
          component={zoomedComponent}
          closeModal={() => {
            setZoomedComponent(undefined);
            closeModal();
          }}
        />
      )}
    </ColumnWrapper>
  );
};

export default StudentComponentsViewColumns;
